import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Observable } from 'rxjs';

import { PrivaButtonComponent } from '@priva/components/button';
import { PrivaContentHeaderComponent } from '@priva/components/content-header';
import { PrivaContentTemplateComponent } from '@priva/components/content-template';
import { PrivaFormComponent } from '@priva/components/form';
import { PrivaFormControlStaticComponent } from '@priva/components/form-control-static';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaFormSectionComponent } from '@priva/components/form-section';
import { PrivaHeaderComponent } from '@priva/components/header';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaNotificationComponent } from '@priva/components/notification';
import { PrivaRadioComponent } from '@priva/components/radio';
import { PrivaLocalizationModule } from '@priva/localization';

import { absoluteRoutePaths } from 'app/core/routing';
import { RegistrationsTableComponent, SigninFormComponent } from 'app/shared/components';
import {
    ApiVersion,
    Credentials,
    defaultTime,
    PersonalUserPreferences,
    Time,
    UserInfo,
} from 'app/shared/models';
import { DeviceInfoStorageService, StorageService } from 'app/shared/services';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';
import { selectApiVersion, selectIdentifier } from 'app/state/api-config/api-config.selectors';
import { resetMasterDataState } from 'app/state/master-data/master-data.actions';
import { selectDeviceProfileIfDefined } from 'app/state/master-data/master-data.selectors';
import { resetMultiUserPreferences } from 'app/state/multi-user-preferences/multi-user-preferences.actions';
import { resetRegistrationState } from 'app/state/registrations/registrations.actions';
import { selectRegistrationError } from 'app/state/registrations/registrations.selectors';

import {
    allowManualSelectionOption,
    AppMode,
    appModeOptions,
    AppSettingsFormInput,
    barcodeInputOption,
    getAppModeOptionName,
    getInputModeName,
    InputMode,
    InputModeOption,
    manualInputOption,
    nfcInputOption,
} from '../models';
import { MultiUserPreferenceService } from '../services';
import { isNfcAvailable, isPersonalScanMode } from '../utils';
import { AppSettingsStore } from './app-settings.store';

const oneMinute = 0.016;

@Component({
    selector: 'app-app-settings-component',
    templateUrl: './app-settings.component.html',
    styleUrl: './app-settings.component.scss',
    providers: [AppSettingsStore],
    imports: [
        ReactiveFormsModule,
        PrivaButtonComponent,
        PrivaContentHeaderComponent,
        PrivaContentTemplateComponent,
        PrivaFormControlStaticComponent,
        PrivaFormGroupComponent,
        PrivaFormComponent,
        PrivaFormSectionComponent,
        PrivaHeaderComponent,
        PrivaLocalizationModule,
        PrivaInputComponent,
        PrivaNotificationComponent,
        PrivaRadioComponent,
        RegistrationsTableComponent,
        SigninFormComponent,
        TranslateModule,
        AsyncPipe,
    ],
})
export class AppSettingsComponent {
    private componentStore = inject(AppSettingsStore);
    private destroyRef = inject(DestroyRef);
    private deviceInfoStorageService = inject(DeviceInfoStorageService);
    preferences = inject(MultiUserPreferenceService);
    private router = inject(Router);
    private storage = inject(StorageService);
    private store = inject(Store);

    ApiVersion = ApiVersion;

    identifier = new FormControl<string>('');
    scanFlowRegistrationSubmitIntervalSec = new FormControl<number>(0, [
        Validators.required,
        Validators.min(0),
    ]);
    appMode = new FormControl<AppMode | null>(null, [Validators.required]);
    inputMode = new FormControl<InputMode | null>(null);
    allowManualSelection = new FormControl<boolean | null>(null);

    formGroup = new FormGroup({
        allowManualSelection: this.allowManualSelection,
        appMode: this.appMode,
        identifier: this.identifier,
        inputMode: this.inputMode,
        scanFlowRegistrationSubmitIntervalSec: this.scanFlowRegistrationSubmitIntervalSec,
        userSessionExpirationTime: new FormControl<Time>(defaultTime()),
    });

    supervisorValidated$ = this.componentStore.supervisorValidated$;
    formIsDirty$: Observable<boolean> = this.componentStore.isFormDirty$;
    apiVersion$ = this.store.select(selectApiVersion);
    registrationError$ = this.store.select(selectRegistrationError);
    deviceProfile$ = this.store.select(selectDeviceProfileIfDefined);
    identifier$ = this.store.select(selectIdentifier);

    enableSaveBtn = signal<boolean>(true);

    constructor() {
        this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const {
                allowManualSelection,
                appMode,
                identifier,
                userSessionExpirationTime,
                inputMode,
                scanFlowRegistrationSubmitIntervalSec,
            } = this.formGroup.getRawValue();

            this.componentStore.setFormValues({
                allowManualSelection: allowManualSelection ?? true,
                appMode: appMode ?? undefined,
                identifier: identifier ?? undefined,
                inputMode: inputMode ?? undefined,
                scanFlowRegistrationSubmitIntervalSec: scanFlowRegistrationSubmitIntervalSec ?? undefined,
                userSessionExpirationTime: userSessionExpirationTime ?? undefined,
            });
        });

        this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((formValues) => {
            if (formValues.inputMode === 'manual') {
                if (this.scanFlowRegistrationSubmitIntervalSec.enabled) {
                    this.scanFlowRegistrationSubmitIntervalSec.disable({ emitEvent: false });
                }
            } else {
                if (!this.scanFlowRegistrationSubmitIntervalSec.enabled) {
                    this.scanFlowRegistrationSubmitIntervalSec.enable();
                }
            }
        });

        /**
         *  By default, users are allowed to select an activity or break manually even in scan mode,
         *  However, not all customers want to allow this option for personal mode + scanning (barcode/nfc)
         *  Therefore, this option is only displayed if these combinations are selected
         */
        this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((formValues) => {
            const isScanMode = formValues.inputMode === 'nfc' || formValues.inputMode === 'barcode';
            const isPersonalScanMode = formValues.appMode === 'personal' && isScanMode;

            if (isPersonalScanMode) {
                if (this.allowManualSelection.disabled) {
                    this.allowManualSelection.enable();
                }
            } else {
                if (this.allowManualSelection.enabled) {
                    this.allowManualSelection.disable({ emitEvent: false });
                }
            }
        });

        this.deviceProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deviceProfile) => {
            if (deviceProfile) {
                if (isPersonalScanMode(deviceProfile.appMode, deviceProfile.inputMode)) {
                    // If there's specific personal scan mode, save is required for the option to select allowManualSelection
                    this.enableSaveBtn.set(true);
                } else {
                    this.enableSaveBtn.set(false);
                }
            } else {
                // As long as no device profile then save button will always be shown
                this.enableSaveBtn.set(true);
            }
        });

        this.componentStore.preloadedSettings$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((appSettings): appSettings is AppSettingsFormInput => !!appSettings),
            )
            .subscribe(
                ({
                    allowManualSelection,
                    appMode,
                    inputMode,
                    userSessionExpirationTime,
                    identifier,
                    scanFlowRegistrationSubmitIntervalSec,
                }: AppSettingsFormInput) => {
                    this.formGroup.setValue({
                        allowManualSelection,
                        appMode,
                        inputMode,
                        userSessionExpirationTime,
                        identifier,
                        scanFlowRegistrationSubmitIntervalSec,
                    });
                },
            );
    }

    save(event: MouseEvent): void {
        event.preventDefault();
        const {
            allowManualSelection,
            appMode,
            identifier,
            userSessionExpirationTime,
            inputMode,
            scanFlowRegistrationSubmitIntervalSec,
        } = this.formGroup.getRawValue();

        // TODO: to be removed once allowManualSelection is added to device profile
        this.deviceProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deviceProfile) => {
            if (deviceProfile) {
                if (allowManualSelection !== null && identifier && userSessionExpirationTime) {
                    this.componentStore.saveSettings({
                        allowManualSelection,
                        appMode: deviceProfile.appMode,
                        identifier,
                        inputMode: deviceProfile.inputMode,
                        scanFlowRegistrationSubmitIntervalSec: deviceProfile.submissionIntervalSeconds,
                        userSessionExpirationTime,
                    });
                }
            }
        });
        if (
            allowManualSelection !== null &&
            appMode &&
            identifier &&
            userSessionExpirationTime &&
            inputMode &&
            (scanFlowRegistrationSubmitIntervalSec || scanFlowRegistrationSubmitIntervalSec === 0)
        ) {
            this.componentStore.saveSettings({
                allowManualSelection,
                appMode,
                identifier,
                inputMode,
                scanFlowRegistrationSubmitIntervalSec,
                userSessionExpirationTime,
            });
        }
    }

    clearPreferences(): void {
        this.store.dispatch(resetMultiUserPreferences());
    }

    async clear() {
        this.clearPreferences();
        this.clearRegistrations();
        this.store.dispatch(resetApiConfig());
        const deviceId = this.deviceInfoStorageService.getOrAddDeviceId();
        this.storage.clear();
        this.deviceInfoStorageService.putDeviceIdToStorage(deviceId);
        await this.router.navigateByUrl(absoluteRoutePaths.setup);
    }

    clearRegistrations(): void {
        this.store.dispatch(resetRegistrationState());
    }

    clearMasterData() {
        this.store.dispatch(resetMasterDataState());
        this.navigateToHomePage();
    }

    allowManualSelectionOptions = allowManualSelectionOption;

    appModeOptions = appModeOptions;

    inputModeOptions: InputModeOption[] = isNfcAvailable()
        ? [manualInputOption, barcodeInputOption, nfcInputOption]
        : [manualInputOption, barcodeInputOption];

    onNewSignin({
        credentials,
        userInfo,
        personalUserPreferences,
    }: {
        credentials: Credentials;
        userInfo: UserInfo | undefined;
        personalUserPreferences: PersonalUserPreferences;
    }) {
        this.componentStore.setValidatedSupervisor({ credentials, userInfo, personalUserPreferences });
    }

    onSupervisorCredentialsValidated() {
        this.componentStore.setSupervisorCredentialsValidated();
    }

    navigateToHomePage() {
        this.router.navigateByUrl('/').then();
    }

    protected readonly getInputModeName = getInputModeName;
    protected readonly getAppModeOptionName = getAppModeOptionName;
    protected readonly isPersonalScanMode = isPersonalScanMode;
}
